import type { ChangeEvent } from "react"
import { useState } from "react"

import { type ApiError, ProgramsService } from "@/client"
import type { ProgramsSearchAvailableUsersResponse } from "@/client/types.gen"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import {
  Box,
  Button,
  CheckboxGroup,
  HStack,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FaPlus, FaSearch } from "react-icons/fa"

import useCustomToast from "@/hooks/useCustomToast"
import { useDebounce } from "@/hooks/useDebounce"
import { handleError } from "@/utils"

interface EnrollUsersModalProps {
  programId: string
  programName: string
  orgId?: string
}

export function EnrollUsersModal({
  programId,
  programName,
}: EnrollUsersModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 20
  const debouncedSearch = useDebounce(searchTerm, 300)

  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const { data: membersData, isLoading: isLoadingMembers } =
    useQuery<ProgramsSearchAvailableUsersResponse>({
      queryKey: [
        "program-available-users",
        programId,
        currentPage,
        debouncedSearch,
      ],
      queryFn: () =>
        ProgramsService.searchAvailableUsers({
          programId,
          skip: (currentPage - 1) * pageSize,
          limit: pageSize,
          search: debouncedSearch || undefined,
        }),
      enabled: isOpen,
      staleTime: 30 * 1000, // 30 seconds
    })

  const availableUsers =
    membersData?.data ?? ([] as ProgramsSearchAvailableUsersResponse["data"])
  const userCount = membersData?.count ?? 0
  const totalPages = Math.ceil(userCount / pageSize)

  const enrollMutation = useMutation({
    mutationFn: (userIds: string[]) =>
      ProgramsService.enrollUsers({
        programId,
        requestBody: { user_ids: userIds },
      }),
    onSuccess: () => {
      showSuccessToast("Users enrolled successfully")
      setSelectedUserIds([])
      setIsOpen(false)
      queryClient.invalidateQueries({
        queryKey: ["program-enrollments", programId],
      })
      queryClient.invalidateQueries({ queryKey: ["program", programId] })
      queryClient.invalidateQueries({ queryKey: ["programs"] })
    },
    onError: (error: ApiError) => {
      handleError(error)
    },
  })

  const handleOpenChange = (details: { open: boolean }) => {
    setIsOpen(details.open)
    if (!details.open) {
      setSelectedUserIds([])
      setSearchTerm("")
      setCurrentPage(1)
    }
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1) // Reset to first page on search
  }

  const showEmptyState = !isLoadingMembers && availableUsers.length === 0

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange} size="lg">
      <DialogTrigger asChild>
        <Button
          colorPalette="blue"
          gap={2}
          data-testid="program-enroll-users-button"
        >
          <FaPlus />
          Enroll Users
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Enroll Users in {programName}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text color="gray.600">
              Search and select users to enroll in this program.
            </Text>

            {/* Search Input */}
            <InputGroup startElement={<FaSearch />}>
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </InputGroup>

            {/* User List */}
            {isLoadingMembers ? (
              <Text>Loading members...</Text>
            ) : showEmptyState ? (
              <Box
                borderWidth="1px"
                borderRadius="md"
                py={6}
                px={4}
                textAlign="center"
              >
                <Text color="gray.500">
                  {searchTerm
                    ? "No users found matching your search."
                    : "All organization members are already enrolled."}
                </Text>
              </Box>
            ) : (
              <>
                <CheckboxGroup
                  value={selectedUserIds}
                  onValueChange={setSelectedUserIds}
                >
                  <VStack align="stretch" maxH="320px" overflowY="auto" gap={2}>
                    {availableUsers.map((member) => (
                      <Box
                        key={member.user_id}
                        borderWidth="1px"
                        borderRadius="md"
                        px={3}
                        py={2}
                      >
                        <Checkbox value={member.user_id}>
                          <VStack align="start" gap={0}>
                            <Text fontWeight="medium">
                              {member.full_name || member.email}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {member.email} • {member.role}
                            </Text>
                          </VStack>
                        </Checkbox>
                      </Box>
                    ))}
                  </VStack>
                </CheckboxGroup>

                {/* Pagination */}
                {totalPages > 1 && (
                  <PaginationRoot
                    count={userCount}
                    pageSize={pageSize}
                    page={currentPage}
                    onPageChange={(details) => setCurrentPage(details.page)}
                  >
                    <HStack justify="center" wrap="wrap" gap={2}>
                      <PaginationPrevTrigger />
                      <PaginationItems />
                      <PaginationNextTrigger />
                    </HStack>
                  </PaginationRoot>
                )}
              </>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter gap={3}>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={enrollMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            colorPalette="blue"
            onClick={() => enrollMutation.mutate(selectedUserIds)}
            disabled={selectedUserIds.length === 0 || enrollMutation.isPending}
            loading={enrollMutation.isPending}
          >
            Enroll {selectedUserIds.length}{" "}
            {selectedUserIds.length === 1 ? "User" : "Users"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
